import { useState, useEffect } from "react";
import Chart from "chart.js/auto";
import { Line } from "react-chartjs-2";
import zoomPlugin from "chartjs-plugin-zoom";
import "chartjs-adapter-moment";
import styles from "./ComparisonGraph.module.css";

Chart.register(zoomPlugin);

function LineComparison({ current, previous, res, xInterval, graphType }) {
    const EMPTY_CHART = { labels: [], datasets: [] };

    const [currentChartData, setCurrentChartData] = useState(EMPTY_CHART);
    const [previousChartData, setPreviousChartData] = useState(EMPTY_CHART);
    const [currentChartOptions, setCurrentChartOptions] = useState({});
    const [previousChartOptions, setPreviousChartOptions] = useState({});
    const [currentMinChartWidth, setCurrentMinChartWidth] = useState("100%");
    const [previousMinChartWidth, setPreviousMinChartWidth] = useState("100%");
    const [currentXRange, setCurrentXRange] = useState({});
    const [previousXRange, setPreviousXRange] = useState({});



    const MIN_TICK_PIXEL = 40;
    const MAX_CANVAS_WIDTH = 20000;





    // ---------- COLORS ----------
    const getRGBA = (index) => {

        const colors = [
            "255, 99, 132",
            "255, 159, 64",
            "255, 205, 86",
            "75, 192, 192",
            "54, 162, 235",
            "153, 102, 255",
            "201, 203, 207",
            "255, 102, 255",
            "102, 255, 102",
            "255, 153, 102",
            "102, 153, 255",
            "153, 255, 153",
        ];

        return colors[index % colors.length];

    };

    // ---------- DATASETS ----------
    const createDatasets = (datasets) =>
        datasets?.map((ds, i) => ({
            ...ds,
            borderWidth: 2,
            pointRadius: 0,
            pointHoverRadius: 4,
            borderColor: `rgba(${getRGBA(i)},1)`,
            fill: graphType === "Fill",
            backgroundColor: (context) => {
                if (graphType !== "Fill") return "rgba(0,0,0,0)";

                const { chart } = context;
                const { ctx, chartArea } = chart;

                // chartArea is undefined on first render
                if (!chartArea) return `rgba(${getRGBA(i)},0.12)`;

                const gradient = ctx.createLinearGradient(
                    0,
                    chartArea.top,
                    0,
                    chartArea.bottom
                );

                gradient.addColorStop(0, `rgba(${getRGBA(i)},0.25)`);
                gradient.addColorStop(1, `rgba(${getRGBA(i)},0.02)`);

                return gradient;
            },
        }));


    const generateCurrentAlignedTicks = () => {
        if (!current?.labels?.length || !xInterval) return [];

        const intervalValue = Number(xInterval.slice(0, -1));
        const unit = xInterval.slice(-1).toUpperCase() === "H" ? "hour" : "minute";

        const start = new Date(current.labels[0]);
        const end = new Date(current.labels.at(-1));

        const ticks = [];
        const currentDate = new Date(start);

        //  ALIGN TO INTERVAL
        if (unit === "minute") {
            currentDate.setSeconds(0, 0);
            currentDate.setMinutes(Math.floor(currentDate.getMinutes() / intervalValue) * intervalValue);
        } else {
            currentDate.setMinutes(0, 0, 0);
            currentDate.setHours(Math.floor(currentDate.getHours() / intervalValue) * intervalValue);
        }

        while (currentDate <= end) {
            ticks.push(new Date(currentDate));
            unit === "hour"
                ? currentDate.setHours(currentDate.getHours() + intervalValue)
                : currentDate.setMinutes(currentDate.getMinutes() + intervalValue);
        }

        return ticks;
    };

    const generatePreviousAlignedTicks = () => {
        if (!previous?.labels?.length || !xInterval) return [];

        const intervalValue = Number(xInterval.slice(0, -1));
        const unit = xInterval.slice(-1).toUpperCase() === "H" ? "hour" : "minute";

        const start = new Date(previous.labels[0]);
        const end = new Date(previous.labels.at(-1));

        const ticks = [];
        const currentDate = new Date(start);

        //  ALIGN TO INTERVAL
        if (unit === "minute") {
            currentDate.setSeconds(0, 0);
            currentDate.setMinutes(Math.floor(currentDate.getMinutes() / intervalValue) * intervalValue);
        } else {
            currentDate.setMinutes(0, 0, 0);
            currentDate.setHours(Math.floor(currentDate.getHours() / intervalValue) * intervalValue);
        }

        while (currentDate <= end) {
            ticks.push(new Date(currentDate));
            unit === "hour"
                ? currentDate.setHours(currentDate.getHours() + intervalValue)
                : currentDate.setMinutes(currentDate.getMinutes() + intervalValue);
        }

        return ticks;
    };


    const calculateCurrentTickCount = () => {

        if (!current?.labels?.length || !xInterval) return 0;

        const intervalValue = Number(xInterval.slice(0, -1));
        const unit = xInterval.slice(-1).toUpperCase() === "H" ? "hour" : "minute";

        const start = new Date(current.labels[0]).getTime();
        const end = new Date(current.labels.at(-1)).getTime();

        const diffMs = end - start;
        const intervalMs = unit === "hour"
            ? intervalValue * 3600000
            : intervalValue * 60000;

        return Math.ceil(diffMs / intervalMs);
    };

    const calculatePreviousTickCount = () => {

        if (!previous?.labels?.length || !xInterval) return 0;
        const intervalValue = Number(xInterval.slice(0, -1));
        const unit = xInterval.slice(-1).toUpperCase() === "H" ? "hour" : "minute";
        const start = new Date(previous.labels[0]).getTime();
        const end = new Date(previous.labels.at(-1)).getTime();
        const diffMs = end - start;
        const intervalMs = unit === "hour"
            ? intervalValue * 3600000
            : intervalValue * 60000;
        return Math.ceil(diffMs / intervalMs);
    }




    // ---------- CURRENT DATA ----------
    useEffect(() => {

        setCurrentXRange({
            min: current?.labels[0],
            max: current?.labels[current.labels.length - 1],
        });

        setCurrentChartData({
            ...current,
            datasets: createDatasets(current?.datasets),
        });



    }, [current, graphType]);

    // ---------- PREVIOUS DATA ----------
    useEffect(() => {

        setPreviousXRange({
            min: previous?.labels[0],
            max: previous?.labels[previous.labels.length - 1],
        });

        setPreviousChartData({
            ...previous,
            datasets: createDatasets(previous?.datasets),
        });




    }, [previous, graphType]);


    useEffect(() => {
        if (!xInterval || !current?.labels?.length) {
            setCurrentMinChartWidth("100%");
            return;
        }

        const ticks = calculateCurrentTickCount();
        const requiredWidth = ticks * MIN_TICK_PIXEL;

        if (requiredWidth <= MAX_CANVAS_WIDTH) {
            setCurrentMinChartWidth(`${requiredWidth}px`);
        } else {
            // width limit reached → expand visible time window instead
            const ratio = requiredWidth / MAX_CANVAS_WIDTH;

            const intervalValue = Number(xInterval.slice(0, -1));
            const unitChar = xInterval.slice(-1).toUpperCase();

            const newInterval =
                unitChar === "H"
                    ? `${Math.ceil(intervalValue * ratio)}H`
                    : `${Math.ceil(intervalValue * ratio)}M`;

            setCurrentXRange({
                min: current?.labels[0],
                max: current?.labels.at(-1),
            });

            setCurrentMinChartWidth(`${MAX_CANVAS_WIDTH}px`);
        }
    }, [xInterval, current]);

    useEffect(() => {
        if (!xInterval || !previous?.labels?.length) {
            setPreviousMinChartWidth("100%");
            return;
        }

        const ticks = calculatePreviousTickCount();
        const requiredWidth = ticks * MIN_TICK_PIXEL;

        if (requiredWidth <= MAX_CANVAS_WIDTH) {
            setPreviousMinChartWidth(`${requiredWidth}px`);
        } else {
            // width limit reached → expand visible time window instead
            const ratio = requiredWidth / MAX_CANVAS_WIDTH;

            const intervalValue = Number(xInterval.slice(0, -1));
            const unitChar = xInterval.slice(-1).toUpperCase();

            const newInterval =
                unitChar === "H"
                    ? `${Math.ceil(intervalValue * ratio)}H`
                    : `${Math.ceil(intervalValue * ratio)}M`;

            setPreviousXRange({
                min: previous?.labels[0],
                max: previous?.labels.at(-1),
            });

            setPreviousMinChartWidth(`${MAX_CANVAS_WIDTH}px`);
        }
    }, [xInterval, previous]);


    const getCuurentXScale = () => {
        if (!xInterval) return { type: "time" };

        const ticks = generateCurrentAlignedTicks();

        return {
            type: "time",
            min: ticks[0],
            max: ticks.at(-1),

            afterBuildTicks: scale => {
                scale.ticks = ticks.map(t => ({ value: t.getTime() }));
            },

            ticks: {
                autoSkip: false,
                callback: function (value, index, ticksArr) {
                    const dt = new Date(ticksArr[index].value);
                    const unit = xInterval.slice(-1).toUpperCase();

                    if (unit === "M") {
                        return dt.toLocaleString([], {
                            day: "2-digit",
                            month: "short",
                            hour: "2-digit",
                            minute: "2-digit"
                        });
                    }

                    if (unit === "H") {
                        return dt.toLocaleString([], {
                            day: "2-digit",
                            month: "short",
                            hour: "2-digit"
                        });
                    }

                    return dt.toLocaleDateString([], {
                        day: "2-digit",
                        month: "short"
                    });
                }
            }
        };
    };

    const getPreviousXScale = () => {
        if (!xInterval) return { type: "time" };

        const ticks = generatePreviousAlignedTicks();

        return {
            type: "time",
            min: ticks[0],
            max: ticks.at(-1),

            afterBuildTicks: scale => {
                scale.ticks = ticks.map(t => ({ value: t.getTime() }));
            },

            ticks: {
                autoSkip: false,
                callback: function (value, index, ticksArr) {
                    const dt = new Date(ticksArr[index].value);
                    const unit = xInterval.slice(-1).toUpperCase();

                    if (unit === "M") {
                        return dt.toLocaleString([], {
                            day: "2-digit",
                            month: "short",
                            hour: "2-digit",
                            minute: "2-digit"
                        });
                    }

                    if (unit === "H") {
                        return dt.toLocaleString([], {
                            day: "2-digit",
                            month: "short",
                            hour: "2-digit"
                        });
                    }

                    return dt.toLocaleDateString([], {
                        day: "2-digit",
                        month: "short"
                    });
                }
            }
        };
    };








    useEffect(() => {
        setCurrentChartOptions({
            responsive: true,
            maintainAspectRatio: false,
            animation: false,
            scales: {
                x: getCuurentXScale(),
            },
            plugins: {
                decimation: {
                    enabled: true,
                    algorithm: "lttb",
                    samples: res ?? 1,
                    threshold: 200,
                },
                zoom: {
                    pan: {
                        enabled: true,
                        mode: "y",
                        modifierKey: "ctrl",
                    },
                    zoom: {
                        wheel: { enabled: true },
                        pinch: { enabled: true },
                        mode: "y",
                    },
                },
                legend: {
                    align: "left",
                    labels: {
                        boxWidth: 10,
                        font: { size: 10 },
                    },
                    onHover: (e, item, legend) => {
                        legend.chart.data.datasets.forEach((ds, i) => {
                            ds._baseColor ??= ds.borderColor;
                            ds.borderWidth = i === item.datasetIndex ? 3 : 1;
                            ds.borderColor =
                                i === item.datasetIndex
                                    ? ds._baseColor
                                    : ds._baseColor.replace(",1)", ",0.25)");
                        });
                        legend.chart.update();
                    },
                    onLeave: (e, item, legend) => {
                        legend.chart.data.datasets.forEach((ds) => {
                            ds.borderWidth = 2;
                            ds.borderColor = ds._baseColor;
                        });
                        legend.chart.update();
                    },
                },
            },
        });
    }, [xInterval, currentMinChartWidth, currentXRange]);



    useEffect(() => {
        setPreviousChartOptions({
            responsive: true,
            maintainAspectRatio: false,
            animation: false,
            scales: {
                x: getPreviousXScale(),
            },
            plugins: {
                decimation: {
                    enabled: true,
                    algorithm: "lttb",
                    samples: res ?? 1,
                    threshold: 200,
                },
                zoom: {
                    pan: {
                        enabled: true,
                        mode: "y",
                        modifierKey: "ctrl",
                    },
                    zoom: {
                        wheel: { enabled: true },
                        pinch: { enabled: true },
                        mode: "y",
                    },
                },
                legend: {
                    align: "left",
                    labels: {
                        boxWidth: 10,
                        font: { size: 10 },
                    },
                    onHover: (e, item, legend) => {
                        legend.chart.data.datasets.forEach((ds, i) => {
                            ds._baseColor ??= ds.borderColor;
                            ds.borderWidth = i === item.datasetIndex ? 3 : 1;
                            ds.borderColor =
                                i === item.datasetIndex
                                    ? ds._baseColor
                                    : ds._baseColor.replace(",1)", ",0.25)");
                        });
                        legend.chart.update();
                    },
                    onLeave: (e, item, legend) => {
                        legend.chart.data.datasets.forEach((ds) => {
                            ds.borderWidth = 2;
                            ds.borderColor = ds._baseColor;
                        });
                        legend.chart.update();
                    },
                },
            },
        });
    }, [xInterval, previousMinChartWidth, previousXRange]);

    // ---------- RENDER ----------
    return (
        <div className={styles.graphWrapper}>
            <div className={styles.graphContainer}>
                <h4 className={styles.graphTitle}>Current Range</h4>
                <div className={styles.chartBox}>
                    <div style={{ width: "100%", height: "100%", overflowX: xInterval ? "auto" : "hidden", overflowY: "hidden" }}>
                        <div style={{ minWidth: xInterval ? currentMinChartWidth : "100%", height: "100%" }}>
                            {currentChartData?.labels?.length > 0 && (
                                <Line data={currentChartData} options={currentChartOptions} />
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div className={styles.graphContainer}>
                <h4 className={styles.graphTitle}>Previous Range</h4>
                <div className={styles.chartBox}>
                    <div style={{ width: "100%", height: "100%", overflowX: xInterval ? "auto" : "hidden", overflowY: "hidden" }}>
                        <div style={{ minWidth: xInterval ? previousMinChartWidth : "100%", height: "100%" }}>
                            {previousChartData?.labels?.length > 0 && (
                                <Line data={previousChartData} options={previousChartOptions} />
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default LineComparison;
